import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/formatDate';
import {
  Plus, RefreshCw, Trash2, Pencil, CheckCircle, AlertCircle, Clock, X,
  UserCheck, UserX, KeyRound, Monitor, Dices,
} from 'lucide-react';

// Человекочитаемые подписи прав. Ключи должны совпадать с
// server/src/services/permissions.js (DEFAULT_PERMISSIONS).
const PERMISSION_GROUPS = [
  {
    title: 'Доступ к разделам',
    items: [
      ['view_overview', 'Обзор'],
      ['view_devices',  'Киоски'],
      ['view_orders',   'Заказы'],
      ['view_stats',    'Статистика'],
      ['view_map',      'Карта'],
    ],
  },
  {
    title: 'Действия со своими киосками',
    items: [
      ['act_prices',         'Менять цены'],
      ['act_payment_toggle', 'Включать/выключать оплату'],
      ['act_paper_count',    'Задавать количество бумаги'],
      ['act_reset_ws',       'Сбрасывать WS-соединение'],
    ],
  },
];

const genPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  const buf = new Uint32Array(10);
  window.crypto.getRandomValues(buf);
  buf.forEach(v => { out += chars[v % chars.length]; });
  return out;
};

/* ── Toast ───────────────────────────────────────────────────────────────── */
const Toast = ({ message, type = 'info', onClose }) => (
  <div className={`toast toast-${type}`}>
    {type === 'success' && <CheckCircle size={14} style={{ color: 'var(--green)', flexShrink: 0 }} />}
    {type === 'error'   && <AlertCircle size={14} style={{ color: 'var(--red)',   flexShrink: 0 }} />}
    {type === 'info'    && <Clock       size={14} style={{ color: 'var(--brand)', flexShrink: 0 }} />}
    <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-1)' }}>{message}</span>
    <button className="btn-icon" style={{ width: '24px', height: '24px', border: 'none' }} onClick={onClose}>
      <X size={12} />
    </button>
  </div>
);

/* ── Create / Edit modal ─────────────────────────────────────────────────── */
// profile = null → создание; иначе редактирование.
const ProfileModal = ({ profile, allDevices, onConfirm, onCancel }) => {
  const isNew = !profile;
  const [username, setUsername]       = useState(profile?.username || '');
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [password, setPassword]       = useState(isNew ? genPassword() : '');
  const [perms, setPerms]             = useState(() => ({ ...(profile?.permissions || {}) }));
  const [deviceIds, setDeviceIds]     = useState(() => new Set((profile?.devices || []).map(d => d.device_id)));
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  const togglePerm = (key) => setPerms(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleDevice = (id) => setDeviceIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleSubmit = async () => {
    if (isNew && !/^[a-zA-Z0-9_.-]{3,32}$/.test(username.trim())) {
      setError('Логин: 3–32 символа, латиница/цифры/._-'); return;
    }
    if ((isNew || password) && password.length < 6) {
      setError('Пароль: минимум 6 символов'); return;
    }
    setSaving(true);
    setError('');
    try {
      await onConfirm({
        id: profile?.id,
        username: username.trim(),
        display_name: displayName.trim(),
        password: password || undefined,
        permissions: perms,
        device_ids: [...deviceIds],
      });
    } catch (err) {
      setError(err.message || 'Ошибка сохранения');
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)', overflowY: 'auto', padding: '24px 0' }}>
      <div className="card" style={{ width: '560px', maxWidth: '94vw', padding: '28px', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '20px' }}>
          {isNew ? 'Создать профиль' : `Профиль: ${profile.username}`}
        </h3>

        {/* Учётные данные */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div style={{ flex: '1 1 160px' }}>
            <label style={{ fontSize: '12px', color: 'var(--text-3)', display: 'block', marginBottom: '5px' }}>
              Логин {isNew && <span style={{ color: 'var(--red)' }}>*</span>}
            </label>
            <input
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="partner1"
              disabled={!isNew}
              style={{ width: '100%', opacity: isNew ? 1 : 0.6 }}
              autoFocus={isNew}
            />
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label style={{ fontSize: '12px', color: 'var(--text-3)', display: 'block', marginBottom: '5px' }}>Отображаемое имя</label>
            <input
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="ИП Иванов / Партнёр"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-3)', display: 'block', marginBottom: '5px' }}>
            {isNew ? 'Пароль' : 'Новый пароль (пусто — не менять)'}
            {isNew && <span style={{ color: 'var(--red)' }}> *</span>}
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={isNew ? 'Минимум 6 символов' : 'Оставьте пустым, чтобы не менять'}
              style={{ flex: 1, fontFamily: 'Consolas, monospace' }}
            />
            <button type="button" className="btn" title="Сгенерировать" onClick={() => setPassword(genPassword())} style={{ gap: '5px' }}>
              <Dices size={13} /> Сген.
            </button>
          </div>
          {!isNew && password && (
            <p style={{ fontSize: '11px', color: 'var(--yellow)', marginTop: '5px' }}>
              ⚠ Смена пароля завершит все активные сессии этого профиля.
            </p>
          )}
        </div>

        {/* Права */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {PERMISSION_GROUPS.map(group => (
            <div key={group.title} style={{ flex: '1 1 220px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                {group.title}
              </p>
              {group.items.map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!perms[key]}
                    onChange={() => togglePerm(key)}
                    style={{ width: '15px', height: '15px', accentColor: 'var(--brand)' }}
                  />
                  {label}
                </label>
              ))}
            </div>
          ))}
        </div>

        {/* Привязка киосков */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
            Привязанные киоски ({deviceIds.size})
          </p>
          {allDevices.length === 0 ? (
            <p className="text-dim" style={{ fontSize: '12px' }}>Нет устройств</p>
          ) : (
            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '10px 12px' }}>
              {allDevices.map(d => {
                const boundElsewhere = d.profile_id && d.profile_id !== profile?.id;
                return (
                  <label key={d.device_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px', fontSize: '13px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={deviceIds.has(d.device_id)}
                      onChange={() => toggleDevice(d.device_id)}
                      style={{ width: '15px', height: '15px', accentColor: 'var(--brand)', flexShrink: 0 }}
                    />
                    <Monitor size={12} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
                    <span style={{ fontWeight: 500 }}>{d.name || d.machine_name || d.device_id.slice(0, 14)}</span>
                    {d.location && <span className="text-dim" style={{ fontSize: '11px' }}>· {d.location}</span>}
                    {boundElsewhere && (
                      <span style={{ fontSize: '11px', color: 'var(--yellow)' }}>
                        — у «{d.profile_display_name || d.profile_username}» (переедет сюда)
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {error && (
          <p style={{ fontSize: '12px', color: 'var(--red)', marginBottom: '14px' }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel} disabled={saving}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            <Plus size={13} /> {saving ? 'Сохранение...' : (isNew ? 'Создать' : 'Сохранить')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Credentials modal (после создания) ──────────────────────────────────── */
const CredentialsModal = ({ creds, onClose }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, backdropFilter: 'blur(6px)' }}>
    <div className="card" style={{ width: '400px', padding: '32px', textAlign: 'center' }}>
      <CheckCircle size={40} style={{ color: 'var(--green)', margin: '0 auto 16px' }} />
      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '20px' }}>Профиль создан</h3>
      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '16px 20px', marginBottom: '18px', textAlign: 'left' }}>
        <p style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '4px' }}>Логин</p>
        <p style={{ fontSize: '18px', fontFamily: 'Consolas, monospace', fontWeight: 700, marginBottom: '12px' }}>{creds.username}</p>
        <p style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '4px' }}>Пароль</p>
        <p style={{ fontSize: '18px', fontFamily: 'Consolas, monospace', fontWeight: 700 }}>{creds.password}</p>
      </div>
      <div style={{ background: 'rgba(255,180,0,0.08)', border: '1px solid rgba(255,180,0,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '24px' }}>
        <p style={{ fontSize: '12px', color: 'var(--yellow)', textAlign: 'left', lineHeight: 1.5 }}>
          ⚠ Передайте данные пользователю. Пароль больше не будет показан — при утере задайте новый через «Изменить».
        </p>
      </div>
      <button className="btn btn-primary" onClick={onClose} style={{ width: '100%' }}>Понятно</button>
    </div>
  </div>
);

/* ── Main ────────────────────────────────────────────────────────────────── */
export const Profiles = () => {
  const { fetchAuthorized } = useAuth();
  const [profiles, setProfiles]   = useState([]);
  const [devices, setDevices]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);   // профиль | 'new'
  const [creds, setCreds]         = useState(null);   // { username, password }
  const [toDelete, setToDelete]   = useState(null);
  const [toasts, setToasts]       = useState([]);
  const toastId = useRef(0);

  const addToast = useCallback((message, type = 'info') => {
    const id = ++toastId.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, dRes] = await Promise.all([
        fetchAuthorized('/api/dashboard/profiles'),
        fetchAuthorized('/api/dashboard/devices'),
      ]);
      const pData = await pRes.json();
      const dData = await dRes.json();
      if (pData.success) setProfiles(pData.profiles);
      if (dData.success) setDevices(dData.devices);
    } catch { addToast('Ошибка загрузки', 'error'); }
    finally { setLoading(false); }
  }, [fetchAuthorized, addToast]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async ({ id, username, display_name, password, permissions, device_ids }) => {
    let profileId = id;
    if (!profileId) {
      const res  = await fetchAuthorized('/api/dashboard/profiles', {
        method: 'POST',
        body: JSON.stringify({ username, password, display_name, permissions }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Ошибка создания');
      profileId = data.profile.id;
    } else {
      const res  = await fetchAuthorized(`/api/dashboard/profiles/${profileId}`, {
        method: 'PUT',
        body: JSON.stringify({ display_name, permissions, ...(password ? { password } : {}) }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Ошибка сохранения');
    }

    const bindRes  = await fetchAuthorized(`/api/dashboard/profiles/${profileId}/devices`, {
      method: 'POST',
      body: JSON.stringify({ device_ids }),
    });
    const bindData = await bindRes.json();
    if (!bindData.success) throw new Error(bindData.error || 'Ошибка привязки киосков');

    setEditing(null);
    if (!id) setCreds({ username, password });
    else addToast('Профиль сохранён', 'success');
    await load();
  };

  const handleToggleActive = async (profile) => {
    try {
      const res  = await fetchAuthorized(`/api/dashboard/profiles/${profile.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: profile.is_active ? 0 : 1 }),
      });
      const data = await res.json();
      if (data.success) {
        addToast(profile.is_active ? 'Профиль отключён, сессии завершены' : 'Профиль включён', 'success');
        await load();
      } else addToast(data.error || 'Ошибка', 'error');
    } catch { addToast('Ошибка соединения', 'error'); }
  };

  const handleDelete = async (profile) => {
    try {
      const res  = await fetchAuthorized(`/api/dashboard/profiles/${profile.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        addToast('Профиль удалён, киоски отвязаны', 'success');
        await load();
      } else addToast(data.error || 'Ошибка удаления', 'error');
    } catch { addToast('Ошибка соединения', 'error'); }
    setToDelete(null);
  };

  const permCount = (p) => Object.values(p.permissions || {}).filter(Boolean).length;
  const permTotal = PERMISSION_GROUPS.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className="fade-in">
      {/* Toasts */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => setToasts(prev => prev.filter(x => x.id !== t.id))} />
        ))}
      </div>

      {editing && (
        <ProfileModal
          profile={editing === 'new' ? null : editing}
          allDevices={devices}
          onConfirm={handleSave}
          onCancel={() => setEditing(null)}
        />
      )}
      {creds && <CredentialsModal creds={creds} onClose={() => setCreds(null)} />}

      {toDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
          <div className="card" style={{ width: '380px', padding: '28px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--red)' }}>
              <AlertCircle size={17} /> Удалить профиль?
            </h3>
            <p style={{ fontSize: '13px', marginBottom: '6px' }}>
              Профиль: <strong>{toDelete.display_name || toDelete.username}</strong>
            </p>
            <p className="text-dim" style={{ fontSize: '12px', marginBottom: '24px' }}>
              Киоски ({toDelete.devices.length}) будут отвязаны и вернутся в общий пул.
              Заказы и статистика киосков не удаляются.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setToDelete(null)}>Отмена</button>
              <button className="btn btn-danger" onClick={() => handleDelete(toDelete)}>
                <Trash2 size={13} /> Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Профили</h1>
          <p>Пользовательские аккаунты с доступом к своим киоскам</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn-icon" onClick={load} disabled={loading} title="Обновить">
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
          <button className="btn btn-primary" onClick={() => setEditing('new')} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={14} /> Создать профиль
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <table>
          <thead>
            <tr>
              <th>Профиль</th>
              <th>Киоски</th>
              <th>Права</th>
              <th>Статус</th>
              <th style={{ width: '200px' }}></th>
            </tr>
          </thead>
          <tbody>
            {profiles.length > 0 ? profiles.map(p => (
              <tr key={p.id} style={{ opacity: p.is_active ? 1 : 0.55 }}>
                <td>
                  <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>
                    {p.display_name || p.username}
                  </div>
                  <div className="text-dim" style={{ fontSize: '11px' }}>
                    @{p.username} · создан {fmtDate(p.created_at)}
                  </div>
                </td>
                <td>
                  {p.devices.length === 0 ? (
                    <span className="text-dim" style={{ fontSize: '12px' }}>нет</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '320px' }}>
                      {p.devices.map(d => (
                        <span key={d.device_id} style={{
                          fontSize: '11px', padding: '2px 8px', borderRadius: '20px',
                          background: 'rgba(129,140,248,0.12)', color: 'var(--brand)',
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                        }}>
                          <Monitor size={9} /> {d.name || d.machine_name || d.device_id.slice(0, 10)}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>
                  <span style={{ fontSize: '12px' }}>
                    {permCount(p)} / {permTotal}
                  </span>
                </td>
                <td>
                  <span style={{ fontSize: '12px', color: p.is_active ? 'var(--green)' : 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span className="dot" style={{ background: p.is_active ? 'var(--green)' : 'var(--text-3)' }} />
                    {p.is_active ? 'Активен' : 'Отключён'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <button
                      className="btn-icon"
                      title="Изменить (имя, права, киоски, пароль)"
                      style={{ width: 'auto', padding: '4px 8px', borderRadius: '6px', gap: '4px', fontSize: '11px', display: 'flex', alignItems: 'center', color: 'var(--brand)' }}
                      onClick={() => setEditing(p)}
                    >
                      <Pencil size={12} /> Изменить
                    </button>
                    <button
                      className="btn-icon"
                      title={p.is_active ? 'Отключить (сессии будут завершены)' : 'Включить'}
                      style={{ width: 'auto', padding: '4px 8px', borderRadius: '6px', gap: '4px', fontSize: '11px', display: 'flex', alignItems: 'center', color: p.is_active ? 'var(--yellow)' : 'var(--green)' }}
                      onClick={() => handleToggleActive(p)}
                    >
                      {p.is_active ? <UserX size={12} /> : <UserCheck size={12} />}
                      {p.is_active ? 'Откл.' : 'Вкл.'}
                    </button>
                    <button className="btn-icon" title="Удалить" style={{ color: 'var(--red)' }} onClick={() => setToDelete(p)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={5} className="empty">
                  {loading ? 'Загрузка...' : 'Профилей нет — создайте первый'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-dim" style={{ fontSize: '12px', marginTop: '14px', maxWidth: '720px' }}>
        <KeyRound size={11} style={{ verticalAlign: '-1px' }} /> Профиль входит в этот же дашборд по логину и паролю
        и видит только привязанные киоски — их статусы, заказы и статистику. Разделы и действия ограничиваются
        правами выше. Разделы «Настройки», «Сервисники», «Журнал дебага», «Сессии» и «Профили» профилям недоступны всегда.
      </p>
    </div>
  );
};
